import { useState, useRef, useEffect, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { createBlankCharacter, initDraft, clearPendingRigFile, setPendingRigFile, setPendingTextureFile, DEFAULT_MESH_SLOTS } from '../lib/characterAuthoringStore'
import { clearCharactersCache } from '../lib/characterState'
import { writeEmptyBlueprintsForCharacter } from '../lib/loadBlueprint'
import { upsertCharacter, uploadRig as aamUploadRig, setMaterialMapping, serverExtractedMaterials, setCharacterSharing } from '../lib/assetManagerApi.js'
import { buildMaterialMappingFromGlb } from '../lib/materialMappingFromGlb.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { AvatarPreviewModel } from './AvatarPreviewModel'

const STEPS = ['Rig', 'Materials', 'Name', 'Preview']

// Role detection aligned with DEFAULT_MESH_SLOTS in characterAuthoringStore.js.
// Order matters: more specific roles first (eyes before face, hair before body).
const ROLE_PATTERNS = [
  { role: 'eyes',   patterns: [/eye/i, /iris/i, /cornea/i, /pupil/i, /sclera/i, /lash/i] },
  { role: 'teeth',  patterns: [/teeth/i, /tooth/i, /gum/i, /saliva/i] },
  { role: 'hair',   patterns: [/hair/i, /scalp/i, /strand/i, /braid/i] },
  { role: 'face',   patterns: [/head/i, /face/i, /brow/i, /ear/i, /forehead/i] },
  { role: 'shoes',  patterns: [/shoe/i, /sneaker/i, /boot/i, /skate/i, /wheel/i] },
  { role: 'outfit', patterns: [/sweater/i, /shirt/i, /jacket/i, /coat/i, /pant/i, /trouser/i, /jeans/i, /sleeve/i, /cloth/i] },
  { role: 'body',   patterns: [/body/i, /skin/i, /neck/i, /torso/i, /arm/i, /leg/i, /hand/i, /foot/i] },
]

function detectRole(name) {
  if (!name) return null
  for (const { role, patterns } of ROLE_PATTERNS) {
    if (patterns.some(p => p.test(name))) return role
  }
  return null
}

const TEXTURE_SLOTS = [
  { key: 'diffuse',   prop: 'map' },
  { key: 'normal',    prop: 'normalMap' },
  { key: 'roughness', prop: 'roughnessMap' },
]

function textureToDataUrl(tex) {
  const img = tex.image
  if (!img) return null
  try {
    const w = img.naturalWidth || img.width || img.videoWidth || 256
    const h = img.naturalHeight || img.height || img.videoHeight || w
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    canvas.getContext('2d').drawImage(img, 0, 0, w, h)
    return canvas.toDataURL('image/jpeg', 0.9)
  } catch {
    return null
  }
}

function sanitizeSlotKey(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
}

async function extractGlbTextures(file) {
  const blobUrl = URL.createObjectURL(file)
  return new Promise(resolve => {
    const dracoLoader = new DRACOLoader()
    dracoLoader.setDecoderPath('/draco/')
    const loader = new GLTFLoader()
    loader.setDRACOLoader(dracoLoader)
    loader.load(blobUrl, gltf => {
      URL.revokeObjectURL(blobUrl)
      const textures = []
      const meshSlotMappings = {}  // slotKey → [meshName] for every mesh in the GLB
      const seenMat = new Map()   // matKey → Set<mapKey> — dedup per material, not per role
      const rolePrimary = new Map() // role → first matName seen for that role

      gltf.scene.traverse(node => {
        if (!node.isMesh) return
        const mats = Array.isArray(node.material) ? node.material : [node.material]
        mats.forEach((mat, matIdx) => {
          if (!mat) return

          // Fully transparent mesh (e.g. cel-shading outline) → hide, skip
          if (mat.transparent && mat.opacity < 0.1) {
            if (node.name) {
              if (!meshSlotMappings.hidden) meshSlotMappings.hidden = []
              if (!meshSlotMappings.hidden.includes(node.name))
                meshSlotMappings.hidden.push(node.name)
            }
            return
          }

          const detectedRole = detectRole(node.name) ?? detectRole(mat.name)
          const matName = mat.name || null

          // Slot key — no fallback to 'body'.
          // Standard role for first material of that role; sanitized material
          // name when same role appears again with a different material.
          let slotKey
          if (detectedRole) {
            if (!rolePrimary.has(detectedRole)) {
              rolePrimary.set(detectedRole, matName)
              slotKey = detectedRole
            } else if (rolePrimary.get(detectedRole) === matName) {
              slotKey = detectedRole
            } else {
              slotKey = matName ? sanitizeSlotKey(matName) : `${detectedRole}_${matIdx}`
            }
          } else {
            slotKey = matName ? sanitizeSlotKey(matName) : `mat_${matIdx}`
          }

          // Record mesh → slot (every mesh, not just non-standard ones)
          if (node.name) {
            if (!meshSlotMappings[slotKey]) meshSlotMappings[slotKey] = []
            if (!meshSlotMappings[slotKey].includes(node.name))
              meshSlotMappings[slotKey].push(node.name)
          }

          // Extract textures — dedup per (material, mapKey)
          const matKey = matName ?? slotKey
          if (!seenMat.has(matKey)) seenMat.set(matKey, new Set())
          const matSeen = seenMat.get(matKey)
          for (const { key: mapKey, prop } of TEXTURE_SLOTS) {
            const tex = mat[prop]
            if (!tex?.image || matSeen.has(mapKey)) continue
            matSeen.add(mapKey)
            const dataUrl = textureToDataUrl(tex)
            if (dataUrl) textures.push({ role: slotKey, mapKey, dataUrl })
          }
        })
      })

      console.log(`[GLB textures] ${textures.length} texture(s):`, textures.map(r => `${r.role}/${r.mapKey}`))
      console.log('[GLB textures] mesh→slot:', Object.entries(meshSlotMappings).map(([s, ns]) => `${s}:[${ns.join(',')}]`).join(' '))
      resolve({ textures, meshSlotMappings })
    }, undefined, err => {
      console.warn('[GLB textures] load error:', err)
      URL.revokeObjectURL(blobUrl)
      resolve({ textures: [], meshSlotMappings: {} })
    })
  })
}

export function CharacterCreationWizard({ onCreated, onCancel }) {
  const [step, setStep] = useState(0)
  const [label, setLabel] = useState('')
  const [faceMorphs, setFaceMorphs] = useState(true)
  // ENG-1028. Defaults to FALSE — private — matching what the server does to a
  // character created any other way (00041 flipped the column default). A
  // wizard that defaulted to shared would quietly undo the feature for the one
  // path most people use, and "the box was already ticked" is not a decision.
  const [shared, setShared] = useState(false)
  const [rigFile, setRigFile] = useState(null)
  const [rigFilename, setRigFilename] = useState(null)
  const [shaderKind, setShaderKind] = useState('pbr')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(true)
  const [extractedTextures, setExtractedTextures] = useState([])
  const [extractedMeshSlots, setExtractedMeshSlots] = useState({})
  const [extracting, setExtracting] = useState(false)
  const rigInputRef = useRef(null)
  const previewBlobUrl = useMemo(() => {
    if (!rigFile) return null
    return URL.createObjectURL(rigFile)
  }, [rigFile])

  useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl)
    }
  }, [previewBlobUrl])

  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  const handleRigPick = file => {
    setRigFile(file)
    setRigFilename(file.name)
    setExtractedTextures([])
    setExtractedMeshSlots({})
    if (!label) {
      const suggested = file.name
        .replace(/\.glb$/i, '')
        .replace(/[_-]+/g, ' ')
        .trim()
        .replace(/\b\w/g, c => c.toUpperCase())
      setLabel(suggested)
    }
    setExtracting(true)
    extractGlbTextures(file).then(({ textures, meshSlotMappings }) => {
      setExtractedTextures(textures)
      setExtractedMeshSlots(meshSlotMappings)
      setExtracting(false)
    })
  }

  const handleFinish = async () => {
    if (!id) return
    setSaving(true)
    setError(null)
    try {
      const baseConfig = createBlankCharacter(id, label, shaderKind)
      baseConfig.faceMorphs = faceMorphs
      if (rigFile) baseConfig._rigFilename = rigFilename

      // Create character in DB
      const created = await upsertCharacter(baseConfig)

      // Upload rig to S3 if provided.
      // Track the File in _pendingRigFile BEFORE the upload attempt so that if
      // it fails, the Sync-to-Cloud step can retry with the same file.
      let rigPath = null
      if (rigFile) {
        setPendingRigFile(rigFile)
        const fd = new FormData()
        fd.append('file', rigFile)
        try {
          const res = await aamUploadRig(id, fd)
          rigPath = res.url
          clearPendingRigFile()
          // ENG-720: the asset-manager now parses the rig and auto-extracts
          // materials/textures + a material_mapping (with materialIds)
          // server-side. Only derive a mapping client-side for older backends
          // that didn't extract — otherwise we'd clobber the server's richer
          // mapping with a slots-only one. Best-effort: never abort creation.
          if (!serverExtractedMaterials(res)) {
            try {
              const mapping = buildMaterialMappingFromGlb(await rigFile.arrayBuffer())
              if (mapping.length) await setMaterialMapping(id, mapping)
            } catch (e) {
              console.warn('[Wizard] material-mapping build failed:', e?.message ?? e)
            }
          }
        } catch (e) {
          console.warn(`[Wizard] rig upload failed — file kept for Sync retry`, e?.message)
        }
      }

      // Write blank blueprint state so the editor starts with an empty canvas
      // (not the conversational.json default) for this character.
      writeEmptyBlueprintsForCharacter(id)

      // Set up in-memory authoring draft with blob URL for immediate preview
      const blobUrl = rigFile ? URL.createObjectURL(rigFile) : null
      const charConfig = { ...baseConfig, ...created, rigPath: rigPath ?? blobUrl }

      // Replace materials with only the slots actually extracted from the GLB.
      // Do NOT merge on top of BLANK_TEMPLATE defaults — custom GLBs have their
      // own material layout and should not inherit phantom slots (eyes, outfit, etc.)
      if (extractedTextures.length > 0) {
        const colorDefaults = {
          body: charConfig.materials?.body?.color ?? '#c8a882',
          face: charConfig.materials?.face?.color ?? '#c8a882',
          hair: charConfig.materials?.hair?.color ?? '#3a2620',
        }
        const materialsFromGlb = {}
        for (const { role, mapKey, dataUrl } of extractedTextures) {
          if (!materialsFromGlb[role]) materialsFromGlb[role] = { kind: shaderKind }
          materialsFromGlb[role][mapKey] = dataUrl
        }
        for (const [role, cfg] of Object.entries(materialsFromGlb)) {
          if (colorDefaults[role] && !cfg.color) cfg.color = colorDefaults[role]
        }
        charConfig.materials = materialsFromGlb
      }

      // Add exact mesh-name patterns for every slot extracted from the GLB so
      // AvatarModel routes each mesh to its correct material (including custom
      // per-material slots like 'mi_wordunity_faceacc').
      if (Object.keys(extractedMeshSlots).length > 0) {
        const updatedSlots = { ...charConfig.meshSlots }
        for (const [role, names] of Object.entries(extractedMeshSlots)) {
          if (updatedSlots[role]) {
            updatedSlots[role] = [...updatedSlots[role], ...names.map(n => `^${n}$`)]
          } else {
            updatedSlots[role] = names.map(n => `^${n}$`)
          }
        }
        charConfig.meshSlots = updatedSlots
      }

      // Persist the corrected materials + meshSlots back to DB.
      // The initial POST used baseConfig (7 default slots); this overwrites with
      // only the slots actually extracted from the GLB.
      if (extractedTextures.length > 0 || Object.keys(extractedMeshSlots).length > 0) {
        const persistConfig = { ...charConfig }
        // Strip blob URLs before saving — rigPath was already uploaded above
        if (persistConfig.rigPath?.startsWith('blob:')) persistConfig.rigPath = null
        await upsertCharacter(persistConfig)
      }

      initDraft(charConfig)

      // ENG-1028. The server creates every character PRIVATE, so sharing is a
      // second call. It runs LAST, after the rig and materials have landed: a
      // failure here then costs nothing already done, and the character is
      // simply still private — the safe direction. Nothing has leaked in the
      // meantime, because private is where it started.
      //
      // Deliberately NOT swallowed like the rig-upload and material-mapping
      // failures above. Those degrade to a retryable Sync; this one would leave
      // the user believing their team can see a character that only they can,
      // which is the one wrong belief this feature must never produce. The
      // throw lands in the catch below and the wizard stays open with the
      // reason; Create Character is idempotent, so retrying is safe (the
      // sharing endpoint returns 200 when the state already matches).
      if (shared) {
        try {
          await setCharacterSharing(id, true)
        } catch (e) {
          throw new Error(
            `character "${id}" was created but could NOT be shared (${e?.body?.error ?? e?.message ?? e}). ` +
            `It is private for now — retry, or share it from the launcher (Properties → Visibility).`,
          )
        }
      }

      clearCharactersCache()

      // Auto-sync the new character so its GLB-embedded textures get
      // promoted from data URLs to proper S3 objects and the blueprint
      // placeholders land in the DB. The wizard stays open ("Syncing…")
      // until sync completes — on success it closes, on failure the
      // user can retry by clicking Create Character again (idempotent
      // upserts handle the partial state).  See Fix #4.
      //
      // Convert each extracted texture's data URL → File →
      // pendingTextureFile so BlueprintEditor.saveToCloud's existing
      // texture upload loop picks them up.
      for (const { role, mapKey, dataUrl } of extractedTextures) {
        try {
          const r = await fetch(dataUrl)
          const blob = await r.blob()
          const ext = (blob.type || '').split('/')[1] || 'png'
          const file = new File([blob], `${role}.${mapKey}.${ext}`, { type: blob.type || 'image/png' })
          setPendingTextureFile(`${role}.${mapKey}`, file)
        } catch (e) {
          console.warn(`[Wizard] failed to prepare ${role}.${mapKey} for upload:`, e?.message ?? e)
        }
      }

      // Hand the character to App so it becomes active, then immediately
      // fire the background sync. The wizard closes (parent unmounts us);
      // the BlueprintEditor's sync-status panel is visible after the
      // wizard closes and shows per-step progress. If sync fails the
      // user retries via File → Sync to Cloud — no need to keep the
      // wizard open. (Awaiting the sync here would update state on an
      // unmounted component, since onCreated closes the wizard.)
      onCreated(charConfig)

      const sync = typeof window !== 'undefined' ? window.__SYNC_TO_CLOUD__ : null
      if (typeof sync === 'function') {
        sync(id).catch(e => console.warn('[Wizard] auto-sync failed:', e?.message ?? e))
      } else {
        console.warn('[Wizard] window.__SYNC_TO_CLOUD__ not available — character was created but textures may not be on S3 yet.')
      }

      setSaving(false)
    } catch (e) {
      setError(`Failed to create character: ${e.message}`)
      setSaving(false)
    }
  }

  const canNext = step === 2 ? id.length > 0 : true
  const canFinish = id.length > 0 && !saving

  // Build a character config for the preview that includes extracted textures.
  const previewCharacter = useMemo(() => {
    const base = {
      material: { kind: shaderKind },
      meshSlots: DEFAULT_MESH_SLOTS,
      materials: {
        body:   { kind: shaderKind === 'toon' ? 'toon' : 'skin', color: '#c8a882' },
        face:   { kind: shaderKind === 'toon' ? 'toon' : 'skin', color: '#c8a882' },
        outfit: { kind: 'pbr' },
        shoes:  { kind: 'pbr' },
        eyes:   { kind: 'eye' },
        teeth:  { kind: 'pbr' },
        hair:   { kind: 'hair', color: '#3a2620' },
      },
    }
    for (const { role, mapKey, dataUrl } of extractedTextures) {
      if (!base.materials[role]) base.materials[role] = {}
      base.materials[role][mapKey] = dataUrl
    }
    return base
  }, [shaderKind, extractedTextures])

  const S = {
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      background: '#111520', border: '1px solid #2a2a3e', borderRadius: 8,
      width: step === 3 ? 900 : 420, padding: 24, fontFamily: 'var(--m-font)',
      maxHeight: '90vh', overflowY: 'auto',
    },
    title: { fontSize: 13, color: '#eee', marginBottom: 20 },
    stepBar: { display: 'flex', gap: 0, marginBottom: 20 },
    stepBtn: active => ({
      flex: 1, textAlign: 'center', padding: '5px 0', fontSize: 9,
      background: active ? '#5b3cc4' : '#1a1a2e',
      color: active ? '#fff' : '#444',
      borderRadius: 0, border: 'none', cursor: 'default',
    }),
    label: { fontSize: 9, color: '#888', marginBottom: 5 },
    input: {
      width: '100%', boxSizing: 'border-box',
      background: '#1a1a28', border: '1px solid #2a2a3e', borderRadius: 4,
      color: '#ddd', padding: '6px 10px', fontSize: 11, fontFamily: 'var(--m-font)',
      outline: 'none', marginBottom: 12,
    },
    idRow: { fontSize: 9, color: '#444', marginBottom: 16 },
    shaderCard: active => ({
      flex: 1, background: active ? '#1a1040' : '#111',
      border: `1px solid ${active ? '#5b3cc4' : '#2a2a3e'}`,
      borderRadius: 6, padding: 12, cursor: 'pointer',
      color: active ? '#c0a0ff' : '#666', fontSize: 10, textAlign: 'center',
    }),
    footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
    btnSecondary: {
      background: 'transparent', border: '1px solid #333', borderRadius: 3,
      color: '#666', padding: '5px 14px', fontSize: 10, cursor: 'pointer', fontFamily: 'var(--m-font)',
    },
    btnPrimary: disabled => ({
      background: disabled ? '#252540' : '#5b3cc4', border: 'none', borderRadius: 3,
      color: disabled ? '#555' : '#fff', padding: '5px 16px', fontSize: 10,
      cursor: disabled ? 'default' : 'pointer', fontFamily: 'var(--m-font)',
    }),
  }

  return (
    // Close only via the Cancel button — NOT backdrop click. An outside-click
    // close fired spuriously while editing fields (e.g. the name), discarding
    // the in-progress character.
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.title}>Create Character</div>

        {/* Step indicator */}
        <div style={S.stepBar}>
          {STEPS.map((s, i) => (
            <div key={s} style={{ ...S.stepBtn(i === step), ...(i === 0 ? { borderRadius: '4px 0 0 4px' } : i === STEPS.length - 1 ? { borderRadius: '0 4px 4px 0' } : {}) }}>
              {i + 1}. {s}
            </div>
          ))}
        </div>

        {/* Step 0: Rig */}
        {step === 0 && (
          <div>
            <div style={S.label}>GLB rig file (optional — you can add it later)</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <button onClick={() => rigInputRef.current?.click()} style={{ ...S.btnSecondary, color: '#aaa', borderColor: '#444' }}>
                Choose GLB…
              </button>
              <div style={{ fontSize: 10, color: '#555' }}>{rigFilename ?? 'none selected'}</div>
            </div>
            <input ref={rigInputRef} type="file" accept=".glb" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleRigPick(f); e.target.value = '' }} />
            <div style={{ fontSize: 9, color: '#444', lineHeight: 1.5 }}>
              The rig will be uploaded to S3 when you click Create Character.
            </div>

          </div>
        )}

        {/* Step 1: Materials */}
        {step === 1 && (
          <div>
            <div style={S.label}>Shader type</div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8, marginBottom: 16 }}>
              <div style={S.shaderCard(shaderKind === 'pbr')} onClick={() => setShaderKind('pbr')}>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>PBR / Skin SSS</div>
                <div style={{ fontSize: 8, opacity: 0.7 }}>Realistic — supports diffuse, normal, roughness, scatter maps</div>
              </div>
              <div style={S.shaderCard(shaderKind === 'toon')} onClick={() => setShaderKind('toon')}>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Toon</div>
                <div style={{ fontSize: 8, opacity: 0.7 }}>Cel-shaded — base color and optional diffuse maps</div>
              </div>
            </div>

            {/* Texture extraction results */}
            <div style={{ borderTop: '1px solid #1e1e30', paddingTop: 14 }}>
              <div style={{ fontSize: 9, color: '#888', marginBottom: 8 }}>Textures from GLB</div>
              {extracting && (
                <div style={{ fontSize: 9, color: '#555' }}>Scanning GLB for textures…</div>
              )}
              {!extracting && extractedTextures.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {extractedTextures.map(({ role, mapKey, dataUrl }) => (
                    <div key={`${role}-${mapKey}`} style={{ textAlign: 'center', width: 72 }}>
                      <img
                        src={dataUrl}
                        alt={`${role} ${mapKey}`}
                        style={{
                          width: 64, height: 64, objectFit: 'cover',
                          borderRadius: 4, border: '1px solid #2a2a3e', display: 'block', marginBottom: 3,
                        }}
                      />
                      <div style={{ fontSize: 7, color: '#5b3cc4', lineHeight: 1.3 }}>{role}</div>
                      <div style={{ fontSize: 7, color: '#555', lineHeight: 1.3 }}>{mapKey}</div>
                    </div>
                  ))}
                </div>
              )}
              {!extracting && rigFile && extractedTextures.length === 0 && (
                <div style={{ fontSize: 9, color: '#444' }}>No embedded textures found in GLB.</div>
              )}
              {!rigFile && (
                <div style={{ fontSize: 9, color: '#444' }}>No GLB selected — textures can be added after creation.</div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Name */}
        {step === 2 && (
          <div>
            <div style={S.label}>Character name</div>
            <input style={S.input} value={label} onChange={e => setLabel(e.target.value)} placeholder="My Character" autoFocus />
            <div style={S.idRow}>ID: {id || '—'} (auto-derived)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" id="wiz-morphs" checked={faceMorphs} onChange={e => setFaceMorphs(e.target.checked)} />
              <label htmlFor="wiz-morphs" style={{ fontSize: 9, color: '#666', cursor: 'pointer' }}>ARKit blendshapes (face morphs)</label>
            </div>
            {/* ENG-1028. Phrased as the ADDITIVE choice ("share") rather than
                "keep private", so the unticked default is the quiet one and
                ticking it is a deliberate act. The consequence is spelled out
                underneath because it is not recoverable by unticking later —
                un-sharing is a separate, guarded action once other people have
                started working on the character. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
              <input
                type="checkbox"
                id="wiz-shared"
                data-testid="wizard-shared"
                checked={shared}
                onChange={e => setShared(e.target.checked)}
              />
              {/* Tokens, not the sibling's inlined hex: this file's literals are
                  count-locked by tests/unit/paletteEnforcement.test.mjs and the
                  count only shrinks. New surfaces use --m-*. */}
              <label htmlFor="wiz-shared" style={{ fontSize: 9, color: 'var(--m-fg-3)', cursor: 'pointer' }}>
                Share with the org
              </label>
            </div>
            <div style={{ fontSize: 8.5, color: 'var(--m-fg-3)', opacity: 0.85, marginTop: 4, lineHeight: 1.5 }}>
              {shared
                ? 'Everyone in the org will see this character and its assets as soon as it is created.'
                : 'Only you will see this character. Share it later from the launcher (Properties → Visibility).'}
            </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 3 && (
          <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
            {/* Left: Summary */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, color: '#888', marginBottom: 12 }}>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ color: '#444', fontSize: 8 }}>NAME</div>
                  <div style={{ color: '#ddd' }}>{label}</div>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ color: '#444', fontSize: 8 }}>SHADER</div>
                  <div style={{ color: '#ddd', textTransform: 'uppercase' }}>{shaderKind}</div>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ color: '#444', fontSize: 8 }}>RIG</div>
                  <div style={{ color: '#ddd' }}>{rigFilename ?? 'Default Myra'}</div>
                </div>
              </div>
            </div>
            {/* Right: 3D Preview Canvas */}
            <div style={{ flex: 1 }}>
              {rigFile ? (
                <Canvas
                  style={{
                    width: '100%',
                    height: 400,
                    background: '#1a1a28',
                    border: '1px solid #2a2a3e',
                    borderRadius: 4,
                  }}
                  camera={{ position: [0, 0, 3], fov: 45, near: 0.1, far: 1000 }}
                  gl={{ antialias: true, alpha: true }}
                >
                  {/* Lights */}
                  <ambientLight intensity={0.6} />
                  <directionalLight position={[5, 8, 5]} intensity={0.8} castShadow />
                  <directionalLight position={[-5, 3, -5]} intensity={0.3} />
                  
                  {/* Grid at y=0 — AvatarPreviewModel grounds the rig's feet to
                      y=0 (mirrors the runtime floor-snap), so the grid sits at
                      the same plane and the character stands on it. */}
                  <gridHelper args={[10, 20]} position={[0, 0, 0]} />
                  
                  {/* Avatar Preview */}
                  <AvatarPreviewModel
                    rigBlobUrl={previewBlobUrl}
                    character={previewCharacter}
                    keepOriginalMaterials={true}
                    onLoaded={() => setPreviewLoading(false)}
                    onError={(e) => {
                      console.error('[AvatarPreviewModel]', e)
                      setPreviewLoading(false)
                    }}
                  />
                </Canvas>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: 400,
                    background: '#1a1a28',
                    border: '1px solid #2a2a3e',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#666',
                    fontSize: 9,
                    textAlign: 'center',
                    padding: 16,
                    boxSizing: 'border-box',
                  }}
                >
                  <div>
                    No rig uploaded — character will use the default Myra rig.<br />
                    You can assign a custom rig later in the authoring panel.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {error && <div style={{ fontSize: 9, color: '#c06060', marginTop: 12 }}>{error}</div>}

        <div style={S.footer}>
          <button onClick={onCancel} style={S.btnSecondary}>Cancel</button>
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)} style={S.btnSecondary}>← Back</button>
          )}
          {step < STEPS.length - 1 ? (
            <button onClick={() => setStep(s => s + 1)} disabled={!canNext} style={S.btnPrimary(!canNext)}>
              Next →
            </button>
          ) : (
            <button onClick={handleFinish} disabled={!canFinish} style={S.btnPrimary(!canFinish)}>
              {saving ? 'Creating…' : 'Create Character'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
